import { Point, Sprite } from 'pixi.js';
import { getAsset } from './preloadGameAssets';
import { Card } from 'components/Card';
import { GamePlace } from 'constants/cards';
import { SolitaireGame } from './SolitaireGame';
import { Layout } from './LayoutManager';
import { CARD_MOVE_TIME } from 'constants/move';
import { COLUMN_Z_INDEX, STOCK_PILE_Z_INDEX, WAST_PILE_Z_INDEX } from 'constants/layout';
import { StockpileButton } from './StockpileButton';
import gsap, { Power1 } from 'gsap';
import { SoundManager } from './core/soundManager';
import { Store } from './store/gameStore';
import { GameName } from 'constants/difficulty';
import { GameEvent } from './events/gameEvent';
import { EventName } from './events/eventsName';
import { WastePile } from './WastePile';
import { GameBoardPlace } from './GameBoardPlace';
import { ActionType } from './quests/types';
import { PlaceholdersAtlas } from './assets/generated';

export class Stockpile extends GameBoardPlace {
    deckButton: StockpileButton;
    disabled = false;
    placeholderSprite?: Sprite;
    boardBlocked = false;
    lastDealTime = 0;

    constructor(parent: SolitaireGame) {
        super(parent, 0, GamePlace.STOCKPILE);
        this.game = parent;
        this.deckButton = new StockpileButton(parent);
        this.deckButton.deckButton.name = 'deckButton';
        GameEvent.add(EventName.BLOCK_ACTION_BUTTONS, this.blockButton.bind(this));

        this.game.wastePile = new WastePile(this.game);
    }
    blockButton(blockTime: number) {
        this.lastDealTime = performance.now() + blockTime;
    }

    start() {
        this.resize();
    }

    reset() {
        if (this.placeholderSprite) {
            this.placeholderSprite.removeFromParent();
            this.placeholderSprite.destroy();
        }

        this.cards = [];
    }
    async drawPlaceholder() {
        if (Store.isFreecell) return;

        const txt = await PlaceholdersAtlas.getTextureByName('slot-empty-restart.png');
        this.placeholderSprite = Sprite.from(txt);
        this.placeholderSprite.name = 'testSprite';
        this.placeholderSprite.anchor.set(0.5);
        const { width, height } = Layout.getCardSize();
        this.placeholderSprite.width = width;
        this.placeholderSprite.height = height;
        this.container.addChild(this.placeholderSprite);
        this.container.position = Layout.getStockPilePosition();
    }
    getCard() {
        const c = this.cards.shift();
        return c;
    }

    putOnTop(card: Card) {
        this.cards.unshift(card);
    }
    checkDisabled(availableCards: number) {
        const disabledState = availableCards === 0;
        if (this.disabled !== disabledState) {
            this.setDisabled(disabledState);
            this.redrawPlaceholder();
        }
    }
    get length() {
        return this.cards.length;
    }
    get isEmpty() {
        return this.cards.length === 0;
    }
    unblock() {
        this.deckButton.block = false;
    }
    block() {
        this.deckButton.block = true;
    }
    async redrawPlaceholder() {
        const { width, height } = Layout.getCardSize();
        const txt = await PlaceholdersAtlas.getTextureByName(
            this.disabled ? 'slot-empty-no-cards.png' : 'slot-empty-restart.png'
        );

        try {
            if (this.placeholderSprite) {
                this.placeholderSprite.texture = txt;
                this.placeholderSprite.width = width;
                this.placeholderSprite.height = height;
            }
        } catch (warn) {
            console.warn(warn);
        }

        this.container.position = Layout.getStockPilePosition();
    }

    repositionPlaceholder() {
        const pos = Layout.getStockPilePosition();
        gsap.to(this.container.position, {
            x: pos.x,
            y: pos.y,
            ease: Power1.easeOut,
            duration: CARD_MOVE_TIME,
        });
    }
    refresh() {
        this.repositionPlaceholder();
        if (Store.tutorialActive) return;

        this.deckButton.drawDeckButton(this.getFrontCardPosition());
        this.cards.forEach((card, index) => {
            card.setPlace({ ...card.place, position: this.getCardPosition(index), order: index });
            card.moveTo(card.place.position, {}, () => {
                this.deckButton.drawDeckButton(this.getFrontCardPosition());
            });
            card.setActive(false);
            card.cardDisplay.castShadow(index === this.length - 1);
        });
    }

    shuffle() {
        this.cards.forEach((card, index) => {
            card.cardDisplay.castShadow(false);
            card.place = {
                position: this.getCardPosition(index),
                zIndex: this.length - index,
                cardPlace: GamePlace.STOCKPILE,
                value: card.place.value,
                order: index,
            };
            card.setVisible(false, true, true);
            card.setActive(false);

            card.moveTo(card.place.position, {}, () => {
                if (index === this.length - 1) this.unblock();
            });
        });
    }

    setDisabled(disabledState: boolean) {
        this.disabled = disabledState;
        this.deckButton.block = disabledState;
        this.refresh();
    }

    resize(): void {
        this.container.position = Layout.getStockPilePosition();
        this.redrawPlaceholder();

        this.cards.forEach((card, index) => {
            card.place = {
                position: this.getCardPosition(index),
                zIndex: this.length - index,
                cardPlace: GamePlace.STOCKPILE,
                value: index,
                order: this.length - index,
            };
            card.setPosition(card.place.position);
        });
        this.deckButton.drawDeckButton(this.getFrontCardPosition());
    }
    getCardPosition(index: number) {
        const positionIndex = Store.isSpider ? 50 - this.length + index : 0;
        return Layout.getStockPilePosition(positionIndex);
    }
    getNextCardPosition(): Point {
        return this.getFrontCardPosition();
    }
    getFrontCardPosition(): any {
        const lastCard = this.cards[0];

        return lastCard ? lastCard.container.position.clone() : Layout.getStockPilePosition();
    }

    addCard(newCard: Card) {
        super.addCard(newCard);
        const zIndex = STOCK_PILE_Z_INDEX - this.length;

        newCard.setPlace({
            cardPlace: GamePlace.STOCKPILE,
            position: Layout.getStockPilePosition(),
            zIndex: zIndex,
            value: 0,
            order: this.length - 1,
        });

        newCard.reset();
        newCard.setPosition(newCard.place.position);
        newCard.setActive(false);
    }

    deal() {
        let dealLimit = Store.getDealCount() * 140;
        if (performance.now() - this.lastDealTime < 0) return;
        this.game.blockMove(dealLimit);
        this.lastDealTime = performance.now() + dealLimit;
        //move to stockPile and wastePile
        Store.startGame();
        if (this.disabled) return;

        if (Store.name === GameName.SPIDER && !Store.tutorialActive) {
            const empty = this.game.columns.find((c) => c.length === 0);
            this.boardBlocked = !!empty;
        }

        if (this.boardBlocked) {
            if (Store.isSpider) {
                const empty = this.game.columns.filter((c) => c.length === 0);
                if (empty.length) {
                    empty.forEach((c) => {
                        c.showBlockAnimation();
                    });
                }
            }
            return;
        }

        this.game.lastMove = performance.now();
        if (this.game.gameStrategy) {
            this.game.wastePile?.cards.forEach(card => {
                card.resetHighlight();
                this.game.moveGroup = this.game.moveGroup.filter(c => c !== card);
            });

            if (this.isEmpty) {
                this.flipTheDeck();
                return;
            }
            if (!this.game.wastePile) this.game.wastePile = new WastePile(this.game);
            const card = this.getCard();
            if (card != null) {
                this.game.moves.add([card]);

                card.setPlace({
                    cardPlace: GamePlace.WASTE_PILE,
                    value: this.game.wastePile.length,
                    zIndex: WAST_PILE_Z_INDEX + this.game.wastePile.length,
                    position: Layout.getWastePilePosition(Math.min(2, this.game.wastePile.length)),
                    order: this.game.wastePile.length,
                });
                card.dealFromStockPile();
                this.game.wastePile.addCard(card);
                card.dropDown();
                SoundManager.play('flipCardSFX');
                
                this.game.wastePile.refresh();
                this.refresh();
            }
            this.game.onExternalClick();
            Store.setGameState(this.game);
            return;
        }

        if (this.isEmpty) {
            this.flipTheDeck(); // deck button should be disabled so no flip for Spider
            return;
        }
        const cardToMove = [] as Card[];
        for (let index = 0; index < Store.getDealCount(); index++) {
            const card = this.getCard();
            if (card) cardToMove.push(card);
        }
        this.game.moves.add([...cardToMove].reverse()); //reverse to keep the order for UNDO
        GameEvent.emit(EventName.DEAL_STOCKPILE);

        switch (Store.name) {
            case GameName.KLONDIKE:
                this.dealKlondike(cardToMove);
                break;
            case GameName.SPIDER:
                this.dealSpider(cardToMove);
                if (this.isEmpty) {
                    this.setDisabled(true);
                    this.redrawPlaceholder();
                }
                break;
        }
        if (Store.tutorialActive) this.game.tutorialManager.onValidMove();
        Store.setGameState(this.game);
    }
    dealKlondike(cardToMove: Card[]) {
        if (!this.game.wastePile) this.game.wastePile = new WastePile(this.game); // it is impossible - only for typescript

        const startIndex = this.game.wastePile.getStartIndex(cardToMove);
        this.game.wastePile.hideInactive(cardToMove.length);

        const lastZIndex = WAST_PILE_Z_INDEX + this.game.wastePile.length;
        const tempCards = [...this.game.wastePile.cards];
        let falseIndex = 0;
        while (cardToMove.length < 3) {
            const c = tempCards.pop();
            if (c) {
                cardToMove.unshift(c);
                falseIndex++;
            } else {
                break;
            }
        }
        cardToMove.forEach((card, index) => {
            const validIndex = index - falseIndex;
            let add = validIndex > -1;
            if (!this.game.wastePile) return; // it is impossible - only for typescript
            if (add) {
                card.setPlace({
                    cardPlace: GamePlace.WASTE_PILE,
                    value: this.game.wastePile.length - 1,
                    zIndex: lastZIndex + validIndex,
                    position: Layout.getWastePilePosition(startIndex + validIndex),
                    order: this.game.wastePile.length - 1,
                });
                card.dealFromStockPile();
                this.game.wastePile.addCard(card);
            }
            card.dropDown();
        });
        SoundManager.play('flipCardSFX');
        this.game.wastePile.refresh();
        this.refresh();
        this.game.autocompleteWorker.isAvailable();
    }
    dealSpider(cardToMove: Card[]) {
        [...cardToMove].reverse().forEach((card) => {
            card.dropDown();
        });
        cardToMove.forEach((card, index) => {
            card.visible = true;
            this.game.columns[index].addCard(card);
            const pos = this.game.columns[index].getColumnLastCardPosition();
            card.setPlace({
                cardPlace: GamePlace.TABLEAU,
                value: index,
                zIndex: COLUMN_Z_INDEX + this.game.columns[index].length,
                position: card.place.position,
                order: this.game.columns[index].length - 1,
            });
            setTimeout(
                (i) => {
                    card.place.position = pos;
                    card.setVisible(true);

                    SoundManager.play('cardPutSFX');
                    card.moveTo(pos, {}, () => {
                        if (i === cardToMove.length - 1) this.game.autocompleteWorker.isAvailable();
                    });
                },
                index * 50,
                index
            );
        });
        setTimeout(() => {
            this.game.refreshBoard();
            this.game.columns.forEach((c) => c.refreshHighlight());
        }, 520);
    }
    flipTheDeck(mute = false) {
        //to stockpile
        if (!this.game.wastePile) return;

        if (!mute) SoundManager.play('flipDeckSFX');
        let i = 0;
        this.game.moves.add(this.game.wastePile.cards);

        while (this.game.wastePile.length) {
            const card = this.game.wastePile.getCard();
            if (card == null) break;
            i++;

            this.putOnTop(card);
            card.setActive(false);
        }
        this.shuffle();
        Store.deckTurns++;

        Store.increaseContextValue('deckTurns').then((context) => {
            GameEvent.emit(EventName.MISSION_ACTION, { type: ActionType.DECK_TURNS, payload: context, currentProgress: context.deckTurns });
        });
    }

    destroy(): void {
        super.destroy();
        this.deckButton.destroy();
        this.container.removeFromParent();
    }
}
